import { useMemo, useState } from 'react';
import {
  ShieldCheck, ShieldAlert, ShieldQuestion, AlertTriangle, CheckCircle2,
  Database, Search, Power, RefreshCw, ArrowUpCircle,
} from 'lucide-react';
import { useMaintenance, useSaveMaintenance } from '@/hooks/useDashboard';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { HostBadge } from '@/components/HostBadge';
import { cn } from '@/lib/utils';
import type { WpSite, WpHealth } from '@/lib/types';
import { useIsMobile } from '@/hooks/useDevice';

// ─── Helpers ───────────────────────────────────────────────────────────────

/** Santé synthétique calculée côté client (miroir de wp-inventory.js). */
function health(s: WpSite): WpHealth {
  if (!s.maintenance_enabled) return 'unknown';
  const threats = s.security?.threats ?? 0;
  const vulns = s.security?.vulnerabilities ?? 0;
  if (threats > 0) return 'critical';
  if (vulns > 0 || (s.updates?.total ?? 0) > 0) return 'attention';
  if (s.backup?.last_at) {
    const ageDays = (Date.now() - new Date(s.backup.last_at).getTime()) / 86_400_000;
    if (ageDays > 8) return 'attention';
  }
  return s.wp?.version ? 'ok' : 'unknown';
}

function relativeDays(iso?: string | null): string {
  if (!iso) return '—';
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);
  if (days <= 0) return "aujourd'hui";
  if (days === 1) return 'hier';
  if (days < 30) return `il y a ${days} j`;
  const months = Math.floor(days / 30);
  return `il y a ${months} mois`;
}

const HEALTH_META: Record<WpHealth, { label: string; variant: 'success' | 'warning' | 'destructive' | 'secondary'; Icon: typeof CheckCircle2 }> = {
  ok: { label: 'OK', variant: 'success', Icon: CheckCircle2 },
  attention: { label: 'Attention', variant: 'warning', Icon: AlertTriangle },
  critical: { label: 'Critique', variant: 'destructive', Icon: ShieldAlert },
  unknown: { label: 'Non suivi', variant: 'secondary', Icon: ShieldQuestion },
};

function HealthBadge({ value }: { value: WpHealth }) {
  const m = HEALTH_META[value];
  return (
    <Badge variant={m.variant} className="gap-1">
      <m.Icon className="h-3 w-3" />
      {m.label}
    </Badge>
  );
}

function UpdatesCell({ s }: { s: WpSite }) {
  if (!s.maintenance_enabled) return <span className="text-muted-foreground">—</span>;
  const total = s.updates?.total ?? 0;
  if (s.updates?.checked_at == null) return <span className="text-muted-foreground text-xs">non collecté</span>;
  if (total === 0) return <Badge variant="success" className="gap-1"><CheckCircle2 className="h-3 w-3" />à jour</Badge>;
  return (
    <Badge variant="warning" className="gap-1">
      <ArrowUpCircle className="h-3 w-3" />{total}
    </Badge>
  );
}

function BackupCell({ s }: { s: WpSite }) {
  if (!s.maintenance_enabled) return <span className="text-muted-foreground">—</span>;
  if (!s.backup?.last_at) return <span className="text-muted-foreground text-xs">aucune</span>;
  const failed = s.backup.status === 'failed';
  return (
    <span className={cn('inline-flex items-center gap-1 text-xs', failed ? 'text-destructive' : 'text-foreground')}>
      <Database className="h-3 w-3" />
      {relativeDays(s.backup.last_at)}
      {failed && <AlertTriangle className="h-3 w-3" />}
    </span>
  );
}

function SecurityCell({ s }: { s: WpSite }) {
  if (!s.maintenance_enabled) return <span className="text-muted-foreground">—</span>;
  const threats = s.security?.threats ?? 0;
  const vulns = s.security?.vulnerabilities ?? 0;
  if (threats > 0) return <Badge variant="destructive" className="gap-1"><ShieldAlert className="h-3 w-3" />{threats} menace(s)</Badge>;
  if (vulns > 0) return <Badge variant="warning" className="gap-1"><AlertTriangle className="h-3 w-3" />{vulns} faille(s)</Badge>;
  return (
    <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
      <ShieldCheck className={cn('h-3.5 w-3.5', s.security?.wordfence ? 'text-success' : 'text-muted-foreground')} />
      {s.security?.wordfence ? 'Wordfence' : 'non protégé'}
    </span>
  );
}

// ─── Vue ─────────────────────────────────────────────────────────────────

const HEALTH_FILTER = [
  { value: 'all', label: 'Toutes santés' },
  { value: 'ok', label: 'OK' },
  { value: 'attention', label: 'Attention' },
  { value: 'critical', label: 'Critique' },
  { value: 'unknown', label: 'Non suivi' },
];

export function Maintenance() {
  const { data, isLoading } = useMaintenance();
  const save = useSaveMaintenance();
  const [local, setLocal] = useState<WpSite[] | null>(null);
  const [filter, setFilter] = useState('');
  const [healthFilter, setHealthFilter] = useState('all');
  const [onlyTracked, setOnlyTracked] = useState(false);
  const isMobile = useIsMobile();

  const sites = local ?? data ?? [];

  const update = (next: WpSite[]) => {
    setLocal(next);
    save.mutate(next);
  };

  const patch = (domain: string, p: Partial<WpSite>) =>
    update(sites.map((s) => (s.domain === domain ? { ...s, ...p } : s)));

  const toggleMaintenance = (s: WpSite) => {
    const enabled = !s.maintenance_enabled;
    update(sites.map((x) => (x.domain === s.domain ? { ...x, maintenance_enabled: enabled, health: enabled ? x.health : 'unknown' } : x)));
  };

  const filtered = useMemo(() => {
    const q = filter.toLowerCase().trim();
    return sites.filter((s) => {
      if (onlyTracked && !s.maintenance_enabled) return false;
      if (healthFilter !== 'all' && health(s) !== healthFilter) return false;
      if (!q) return true;
      return [s.domain, s.client, s.notes].filter(Boolean).some((v) => String(v).toLowerCase().includes(q));
    });
  }, [sites, filter, healthFilter, onlyTracked]);

  const stats = useMemo(() => {
    const tracked = sites.filter((s) => s.maintenance_enabled);
    return {
      tracked: tracked.length,
      total: sites.length,
      updates: tracked.reduce((n, s) => n + (s.updates?.total ?? 0), 0),
      backupsOk: tracked.filter((s) => s.backup?.last_at && (Date.now() - new Date(s.backup.last_at).getTime()) / 86_400_000 <= 8).length,
      alerts: tracked.reduce((n, s) => n + (s.security?.threats ?? 0) + (s.security?.vulnerabilities ?? 0), 0),
    };
  }, [sites]);

  if (isLoading) return <Skeleton className="h-96" />;

  return (
    <div className="space-y-4">
      {/* KPIs */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Kpi icon={ShieldCheck} label="Sites suivis" value={`${stats.tracked}`} sub={`/ ${stats.total} au total`} tone="info" />
        <Kpi icon={ArrowUpCircle} label="Mises à jour en attente" value={`${stats.updates}`} tone={stats.updates > 0 ? 'warning' : 'success'} />
        <Kpi icon={Database} label="Sauvegardes < 8 j" value={`${stats.backupsOk}`} sub={`/ ${stats.tracked} suivis`} tone={stats.backupsOk < stats.tracked ? 'warning' : 'success'} />
        <Kpi icon={ShieldAlert} label="Alertes sécurité" value={`${stats.alerts}`} tone={stats.alerts > 0 ? 'destructive' : 'success'} />
      </div>

      {/* Barre d'outils */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Filtrer par domaine, client, notes…" className="pl-8" />
        </div>
        <Select value={healthFilter} onValueChange={setHealthFilter}>
          <SelectTrigger className="h-9 w-40 text-sm"><SelectValue /></SelectTrigger>
          <SelectContent>{HEALTH_FILTER.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
        </Select>
        <button
          type="button"
          onClick={() => setOnlyTracked((v) => !v)}
          className={cn(
            'inline-flex h-9 items-center gap-1.5 rounded-md border px-3 text-sm transition-colors',
            onlyTracked ? 'border-primary/40 bg-primary/10 text-primary' : 'border-input text-muted-foreground hover:text-foreground'
          )}
        >
          <Power className="h-3.5 w-3.5" /> Suivis uniquement
        </button>
      </div>

      {/* Bandeau d'information Phase 1 */}
      <div className="flex items-start gap-2 rounded-md border border-info/30 bg-info/10 px-3 py-2 text-xs text-info">
        <RefreshCw className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        <span>
          Socle initialisé depuis l'inventaire des sites. Active la maintenance sur les sites concernés via le bouton <Power className="inline h-3 w-3" />.
          La collecte automatique (versions, mises à jour, sauvegardes Google&nbsp;Drive, scans sécurité) sera branchée aux phases suivantes (MainWP&nbsp;/ WP-CLI).
        </span>
      </div>

      {/* Tableau / cards */}
      {isMobile ? (
        <div className="space-y-2">
          {filtered.map((s) => <SiteCard key={s.domain} s={s} onToggle={() => toggleMaintenance(s)} onNotes={(v) => patch(s.domain, { notes: v })} />)}
        </div>
      ) : (
        <Card className="overflow-hidden">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Domaine</TableHead>
                    <TableHead>Hébergeur</TableHead>
                    <TableHead className="text-center">Suivi</TableHead>
                    <TableHead>WordPress</TableHead>
                    <TableHead>MàJ</TableHead>
                    <TableHead>Sauvegarde</TableHead>
                    <TableHead>Sécurité</TableHead>
                    <TableHead>Santé</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((s) => (
                    <TableRow key={s.domain} className={cn(!s.maintenance_enabled && 'opacity-55')}>
                      <TableCell>
                        <div className="font-medium">{s.domain}</div>
                        {s.client && <div className="text-xs text-muted-foreground">{s.client}</div>}
                      </TableCell>
                      <TableCell><HostBadge host={s.host} /></TableCell>
                      <TableCell className="text-center">
                        <button
                          type="button"
                          onClick={() => toggleMaintenance(s)}
                          title={s.maintenance_enabled ? 'Maintenance active — cliquer pour désactiver' : 'Activer la maintenance'}
                          className={cn(
                            'inline-flex h-7 w-7 items-center justify-center rounded-full border transition-colors',
                            s.maintenance_enabled ? 'border-success/40 bg-success/15 text-success' : 'border-input text-muted-foreground hover:text-foreground'
                          )}
                        >
                          <Power className="h-3.5 w-3.5" />
                        </button>
                      </TableCell>
                      <TableCell className="text-sm num">{s.wp?.version ?? <span className="text-muted-foreground">—</span>}</TableCell>
                      <TableCell><UpdatesCell s={s} /></TableCell>
                      <TableCell><BackupCell s={s} /></TableCell>
                      <TableCell><SecurityCell s={s} /></TableCell>
                      <TableCell><HealthBadge value={health(s)} /></TableCell>
                    </TableRow>
                  ))}
                  {filtered.length === 0 && (
                    <TableRow><TableCell colSpan={8} className="py-10 text-center text-muted-foreground">Aucun site ne correspond aux filtres.</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      <p className="text-xs text-muted-foreground">
        {filtered.length} site(s) affiché(s). Les modifications (suivi, notes) sont sauvegardées automatiquement.
      </p>
    </div>
  );
}

function Kpi({ icon: Icon, label, value, sub, tone }: {
  icon: typeof ShieldCheck; label: string; value: string; sub?: string;
  tone: 'info' | 'success' | 'warning' | 'destructive';
}) {
  const toneCls = {
    info: 'text-info',
    success: 'text-success',
    warning: 'text-warning',
    destructive: 'text-destructive',
  }[tone];
  return (
    <Card>
      <CardContent className="flex items-center gap-3 p-4">
        <div className={cn('shrink-0', toneCls)}><Icon className="h-6 w-6" /></div>
        <div className="min-w-0">
          <div className={cn('text-2xl font-bold num leading-none', toneCls)}>{value}</div>
          <div className="mt-1 text-xs text-muted-foreground truncate">{label}{sub && <span className="opacity-70"> {sub}</span>}</div>
        </div>
      </CardContent>
    </Card>
  );
}

function SiteCard({ s, onToggle, onNotes }: { s: WpSite; onToggle: () => void; onNotes: (v: string | null) => void }) {
  return (
    <Card className={cn(!s.maintenance_enabled && 'opacity-70')}>
      <CardContent className="space-y-2 p-3">
        <div className="flex items-center justify-between gap-2">
          <div className="min-w-0">
            <div className="font-medium truncate">{s.domain}</div>
            {s.client && <div className="text-xs text-muted-foreground truncate">{s.client}</div>}
          </div>
          <button
            type="button"
            onClick={onToggle}
            className={cn(
              'inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full border transition-colors',
              s.maintenance_enabled ? 'border-success/40 bg-success/15 text-success' : 'border-input text-muted-foreground'
            )}
          >
            <Power className="h-3.5 w-3.5" />
          </button>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <HostBadge host={s.host} />
          <HealthBadge value={health(s)} />
          <UpdatesCell s={s} />
        </div>
        <div className="flex items-center gap-3 text-xs text-muted-foreground">
          <BackupCell s={s} />
          <SecurityCell s={s} />
        </div>
        <Input
          defaultValue={s.notes ?? ''}
          onBlur={(e) => onNotes(e.target.value || null)}
          className="h-8 text-sm"
          placeholder="Notes…"
        />
      </CardContent>
    </Card>
  );
}
